/**
 * Project setup redesign — Track B (mailbox wizard + Mailboxes page): namespaces `projectSetupMailbox`, `settingsMailboxes`.
 *
 * Top-level namespaces owned by this track only. `mailboxSetupNl` is typed against
 * `mailboxSetupEn`, so a key missing in Dutch fails typecheck exactly as it does in
 * the main dictionary.
 *
 * Failures reuse Track A's `oauthReturn.reason.<code>` (one sentence per
 * `SetupReasonCode`, EN and NL); the raw code only ever appears under
 * "Technical details" (`oauthReturn.technicalDetails`).
 */
export const mailboxSetupEn = {
  projectSetupMailbox: {
    title: "Connect a mailbox",
    intakeOther: "Other work sources",
    progress: "Step {current} of {total}",
    progressDone: "Finished",
    dialogLabel: "Connect a mailbox to {project}",
    back: "Back",
    next: "Continue",
    cancel: "Cancel",
    close: "Close",
    loading: "Loading…",
    loadError: "The mailbox options could not be loaded. Check your connection and try again.",
    retry: "Try again",
    technicalCode: "Code: {code}",
    providerName: {
      gmail: "Google",
      office365: "Microsoft",
      internal: "Forwarding",
      forward: "FixControl address",
    },
    providerLabel: {
      gmail: "Gmail / Google Workspace",
      office365: "Microsoft 365 / Outlook",
      internal: "Forwarded mail",
      forward: "Forward to FixControl",
    },
    fieldError: {
      address_required: "Enter the address.",
      address_invalid: "Enter a full email address, like support@company.com.",
      shared_invalid: "Enter a full email address, like support@company.com.",
      account_required: "Choose a mailbox to continue.",
      existing_required: "Choose a mailbox to continue.",
      display_name_required: "Enter a name.",
    },
    fieldLabel: {
      address: "Mailbox address",
    },
    start: {
      heading: "Which mailbox should receive work for this project?",
      intro: "Mail that arrives in the mailbox becomes work in {project}. By default, a reply only goes out when a person sends or approves it.",
      newTitle: "Connect a new mailbox",
      newBody: "Sign in with Microsoft 365 or Google, or forward mail from another address.",
      existingTitle: "Use an existing company mailbox",
      existingBody: "Pick a mailbox your organization already connected.",
      existingCount: "{count} available",
      existingNone: "None available yet",
      newOrgOnly: "Needs an organization administrator.",
      existingOnly: "You can connect this project to mailboxes your organization already has. Adding a new Google or Microsoft account needs an organization administrator.",
      existingOnlyNamed: "You can connect this project to mailboxes your organization already has. Adding a new Google or Microsoft account needs an organization administrator: {names}.",
    },
    provider: {
      heading: "Where does this mailbox live?",
      intro: "Choose the service your mailbox runs on. You sign in on its own page; FixControl never sees your password.",
      hint: {
        office365: "Work or school accounts, Outlook.com and shared mailboxes.",
        gmail: "Google Workspace and Gmail accounts.",
        internal: "Forward mail from an address into a mailbox FixControl already reads. It is sorted by the address it was sent to.",
        forward: "No sign-in needed. FixControl gives you an address, and you forward your mail to it from Gmail, Outlook or any other mail service.",
      },
      notConfigured: "Signing in with {provider} is not set up on this installation yet. The person who runs FixControl for your organization can turn it on.",
      askAdmin: "Ask your administrator",
      askAdminBody: "Ask the person who manages FixControl for your organization to set up signing in with {provider}. Until then you can use a mailbox that is already connected.",
      useExisting: "Use an existing mailbox instead",
      noCreateRight: "Only people who may add company connections can sign in a new mailbox. Ask an administrator of your organization, or use a mailbox that is already connected.",
      internalNeedsAccount: "Available once a Microsoft or Google mailbox is connected to your organization.",
      internalOrgOnly: "Only an organization administrator can add a new forwarding address. You can use one your organization already has.",
      forwardOrgOnly: "Only an organization administrator can add a new mailbox. You can use one your organization already has.",
      unavailable: "Not available",
    },
    explain: {
      heading: "Before you continue to {provider}",
      intro: "You will sign in on {provider}'s own page. FixControl never sees your password.",
      permissionsTitle: "What FixControl will ask for",
      permissions: {
        office365: "FixControl will ask Microsoft for permission to read messages in the mailbox you select and, if you allow it, send replies through it. You can revoke this later.",
        gmail: "FixControl will ask Google for permission to read messages in the mailbox you select and, if you allow it, send replies through it. You can revoke this later.",
      },
      neverDelete: "FixControl never deletes mail.",
      scopeTitle: "Company or project?",
      scope: "This connection belongs to your organization. You are connecting one of its mailboxes to this project.",
      continue: {
        office365: "Continue to Microsoft",
        gmail: "Continue to Google",
      },
      leaving: "You will come back to this step after signing in.",
    },
    select: {
      heading: "Which mailbox?",
      intro: "{provider} is connected. Choose the mailbox that should receive work for {project}.",
      accountsLabel: "Mailboxes you can use",
      signedInAs: "Signed in as {address}",
      justConnected: "Just connected",
      sharedLabel: "Shared mailbox (optional)",
      sharedHint: "To use a shared mailbox this account can open, enter its address. Leave empty to use {address}.",
      internalHeading: "Which address forwards mail?",
      internalIntro: "Enter the address your customers write to. Set up forwarding from it into a mailbox FixControl already reads.",
      internalLabel: "Address that forwards mail",
      internalPlaceholder: "support@company.com",
      forwardHeading: "Which address do your customers write to?",
      forwardIntro: "Enter your support address. After connecting, FixControl gives you an address to forward that mail to.",
      forwardLabel: "Your support address",
      noAccount: "We could not find the account you just signed in with. Sign in again, or ask an administrator to check the connection.",
      signInAgain: "Sign in again",
      usedBy: "Used by {project}",
      usedByOther: "Used by another project",
      usedHere: "Already connected to this project",
      needsReconnect: "Sign-in no longer works",
      oauthHeading: "Signing in with {provider} did not finish",
      tryAgain: "Try again",
      reviewPermissions: "Review permissions",
    },
    existing: {
      heading: "Choose a company mailbox",
      intro: "These mailboxes are already connected to your organization. A mailbox can deliver work to one project at a time.",
      listLabel: "Company mailboxes",
      empty: "Your organization has no mailbox that can be used here yet.",
      connectNew: "Connect a new mailbox",
      accountNotUsed: "Connected {provider} account, not used by a project yet",
      system: "FixControl system mailbox",
      internalBadge: "Forwarded",
    },
    behavior: {
      heading: "How should this mailbox work in {project}?",
      intake: "New mail becomes work in {project}",
      intakeHelp: "This is what connecting a mailbox to a project does, so it is always on.",
      replies: "Allow replies through this mailbox",
      repliesHelpOff: "Off: FixControl only reads mail. You can turn this on later.",
      repliesHelpOn: "By default a person sends each reply. Whether a reply must be approved first, and whether the AI may send by itself, is set on the project's Approvals & rules page under Replies to customers (company-wide defaults: Customer communication → Sending & approval).",
      repliesInternal: "Replies can't be sent from a forwarding address.",
      repliesForward: "Replies go out from this mailbox's FixControl address, under the name below. Your customer's answer comes straight back here.",
      replyIdentityForward: "{name} <this mailbox's FixControl address>",
      repliesShared: "This setting belongs to the company mailbox, so it also applies where else it is used.",
      displayName: "Name shown to colleagues",
      displayNameHelp: "For example \"Guest support\". Shown in the inbox and on work items.",
      replyIdentity: "Replies are sent as",
      replyIdentityOff: "Replies are off, so nothing is sent from this address.",
      connect: "Connect and test",
    },
    test: {
      heading: "Testing the connection",
      intro: "FixControl checks that it can use {address} for {project}.",
      running: "Checking…",
      allGood: "Everything works.",
      warnOnly: "It works. There is something to keep an eye on.",
      waitingFirst: "Set up, waiting for the first email. Send a message to {address} to prove that it arrives.",
      failed: "Something needs your attention before mail can arrive.",
      testAgain: "Test again",
      finish: "Finish",
      finishLater: "Close and fix later",
      connectError: "The mailbox could not be connected.",
      runError: "The test could not run just now.",
      linkedElsewhere: "This address already receives work for {project}. Disconnect it there first, or choose another mailbox.",
    },
    forward: {
      title: "Forward your mail to this address",
      copy: "Copy",
      copied: "Copied",
      gmailTitle: "In Gmail",
      gmailSteps: "Settings → See all settings → Forwarding and POP/IMAP → Add a forwarding address, and paste the address above. Gmail then sends a confirmation code to FixControl, and it appears here. Enter it in Gmail and choose \"Forward a copy of incoming mail\".",
      gmailCode: "Gmail sent confirmation code {code}. Enter it in Gmail's forwarding settings, or confirm with the link.",
      gmailLinkOnly: "Gmail sent a confirmation link. Open it to start forwarding.",
      gmailConfirmLink: "Confirm in Gmail",
      outlookTitle: "In Microsoft 365 / Outlook",
      outlookSteps: "Add a rule that forwards incoming mail to the address above, or ask your administrator to set forwarding on the mailbox. Your administrator may need to allow forwarding to outside addresses.",
      keepCopy: "Keep a copy in your own mailbox: FixControl works on the forwarded copy.",
    },
    check: {
      label: {
        connection: "Sign-in works",
        mailbox_readable: "Can read messages",
        address_matches_connection: "Address belongs to this sign-in",
        ingest_enabled: "Receiving mail is on",
        project_resolution: "New mail arrives in this project",
        recent_ingest: "Mail received recently",
        reply_channel: "Replies can be sent",
        provider_health: "Connection works",
        test_message: "Test message",
      },
      status: {
        pass: "OK",
        warn: "Heads-up",
        fail: "Needs attention",
        skipped: "Not needed",
      },
      message: {
        connection: {
          pass: "FixControl is signed in.",
          skipped: "Skipped until the sign-in works.",
          internal: "Not needed for a forwarding address.",
          forward: "Not needed: your mail is forwarded to FixControl.",
        },
        mailbox_readable: {
          pass: "FixControl can read new messages in this mailbox.",
          skipped: "Skipped until the sign-in works.",
          internal: "Not needed for a forwarding address.",
          forward: "Not needed: your mail is forwarded to FixControl.",
        },
        address_matches_connection: {
          pass: "This address is the mailbox the sign-in reads.",
          skipped: "Skipped until the sign-in works.",
          internal: "Not needed for a forwarding address.",
          forward: "Not needed: your mail is forwarded to FixControl.",
        },
        ingest_enabled: {
          pass: "New mail to this address is picked up.",
        },
        project_resolution: {
          pass: "New mail to this address becomes work in this project.",
        },
        recent_ingest: {
          pass: "Mail arrived recently.",
          never: "No mail has arrived yet. Send an email to this address to see it arrive.",
          stale: "No mail has arrived in the last 7 days.",
          waiting_first: "No mail has arrived yet, so nothing proves the forwarding works. Send an email to this address and check that it arrives.",
        },
        reply_channel: {
          pass: "Approved replies can be sent.",
          replies_off: "Replies are off for this mailbox.",
          skipped: "Replies are off for this mailbox.",
        },
      },
    },
    fix: {
      reconnect: "Reconnect",
      review_permissions: "Fix permissions",
      choose_mailbox: "Choose another mailbox",
      enable_ingest: "Turn on receiving mail",
      link_project: "Connect to this project",
      configure_outbound: "Set up outgoing email",
      confirm_test_message: "Send a test message",
      retry: "Try again",
    },
    done: {
      heading: "Mailbox connected",
      body: "{address} is now connected to {project}.",
      bodyAttention: "{address} is connected to {project}, but still needs attention. You can fix it from the mailbox card.",
      bodyWaiting: "{address} is connected to {project}. It is not proven yet: send an email to {address} and check that it arrives.",
      next: "Next: {step}",
      done: "Done",
    },
    cards: {
      title: "Mailboxes",
      empty: "No mailbox connected yet.",
      emptyBody: "Receive customer email as work in this project.",
      connect: "Connect mailbox",
      connectAnother: "Connect another mailbox",
      viewProjectInbox: "View project inbox",
      usedFor: "Used for: {list}",
      usedForIntake: "Incoming work",
      usedForReplies: "Approved replies",
      lastChecked: "Last checked {when}",
      neverChecked: "Not checked yet",
      test: "Test connection",
      manage: "Manage",
      disconnect: "Disconnect from this project",
      noRight: "Only an administrator of your organization or of this project can connect a mailbox. Ask one of them.",
      noRightNamed: "Only an administrator of your organization ({names}) or of this project can connect a mailbox. Ask one of them.",
      readOnly: "You can see this mailbox. Only an administrator of your organization or of this project can test or change it.",
      waitingHint: "Send an email to {address} to prove that forwarding works.",
      status: {
        working: "Working",
        waiting: "Set up, waiting for the first email",
        untested: "Not tested yet",
        attention: "Needs attention",
        reconnect: "Reconnect required",
        disabled: "Disabled",
      },
      manageTitle: "Manage {address}",
      manageIntro: "How this mailbox works in {project}. The name and whether replies are allowed belong to the company mailbox itself, so a change here is what everyone in your organization sees.",
      save: "Save",
      saved: "Saved",
      disconnectTitle: "Disconnect {address} from {project}?",
      disconnectBody: "New mail to this address stops arriving in this project. The company mailbox itself stays connected, and other projects are not affected.",
      disconnectConfirm: "Disconnect",
      testResultTitle: "Test result",
      hideResult: "Hide result",
    },
  },
  settingsMailboxes: {
    eyebrow: "Communication",
    title: "Mailboxes",
    subtitle: "Every mailbox your organization uses: where its mail comes from, whether it may send approved replies, and which project its mail goes to. Mailboxes are shared by the whole company; you choose per project which one delivers work there.",
    loading: "Loading…",
    loadError: "The mailboxes could not be loaded (HTTP {status}).",
    loadErrorGeneric: "The mailboxes could not be loaded.",
    count: "{count} mailboxes",
    countOne: "1 mailbox",
    outboundChannel: "Outgoing email: {provider}",
    dryRun: "test mode",
    newMailbox: "New mailbox",
    cancel: "Cancel",
    create: {
      title: "New mailbox",
      sub: "Shared mailboxes are visible to the whole company. A personal mailbox belongs to one person.",
      fromProject: "Connecting a mailbox for a project? Use the guided setup in the project instead.",
      openWizard: "Open guided setup",
      submit: "Create mailbox",
      error: "The mailbox could not be created.",
      linkFailed: "The mailbox was created, but connecting it to the project failed: {error}",
      noProviders: "No Microsoft or Google mailbox is connected yet. Connect one under Channels first, or create a forwarding address.",
      connectProviders: "Go to Channels",
    },
    field: {
      kind: "Type",
      provider: "Where mail comes from",
      email: "Email address",
      displayName: "Name shown to colleagues",
      visibility: "Who can see it",
      owner: "Owner",
      ownerHelp: "Required for a personal or private mailbox.",
      ownerNone: "No owner",
      ownerMe: "Me ({name})",
      ownerUnavailable: "Only you can be chosen here; an administrator can pick any colleague.",
      project: "Connect to project (optional)",
      projectNone: "No project",
      projectHelp: "New mail to this address then becomes work in that project.",
      color: "Colour",
      signature: "Signature (optional, plain text)",
      autoAssign: "Automatic assignment",
    },
    kind: {
      shared: "Shared",
      personal: "Personal",
      system: "System",
    },
    provider: {
      gmail: "Gmail / Google Workspace",
      office365: "Microsoft 365 / Outlook",
      internal: "Forwarded (no sending)",
      forward: "Forwarded to FixControl",
      connectedAs: "{provider} ({addresses})",
    },
    visibility: {
      tenant: "Whole company",
      team: "Team (same as whole company for now)",
      private: "Private (owner only)",
      tenantShort: "Whole company",
      teamShort: "Team",
      privateShort: "Private",
    },
    toggle: {
      send: "Allow replies",
      sendHelp: "Replies may go out from this address. Approval and AI sending are set per project under Approvals & rules → Replies to customers, with company-wide defaults under Customer communication → Sending & approval.",
      sendInternal: "A forwarding address never sends.",
      sendForward: "Replies go out from this mailbox's FixControl address, under its name.",
      ingest: "Receive mail",
      ingestHelp: "Off: new mail is ignored; the mailbox stays for history.",
      autoCreateIssues: "Create issues automatically",
      autoCreateIssuesHelp: "When FixControl detects a clear bug, feature request or other actionable engineering task, it can automatically create an issue in the linked project. Support questions and irrelevant mail remain in the inbox.",
      autoCreateOff: "Off — you create an issue yourself",
      autoCreateEngineering: "Bugs and feature requests",
      autoCreateAll: "Every customer request",
      autoCreateOffHelp: "Nothing becomes an issue by itself. Use \"Create issue\" on a mail when you want one.",
      autoCreateEngineeringHelp: "A clear bug or feature request becomes an issue in the linked project. Questions stay in the inbox.",
      autoCreateAllHelp: "Every customer request becomes an issue in the linked project, questions included; a question still gets the AI's reply suggestion. Spam, auto-replies and a bare \"thanks\" never do, and a conversation that already has an issue never gets a second one.",
    },
    card: {
      edit: "Edit",
      save: "Save",
      saved: "Saved",
      delete: "Delete",
      deleteConfirm: "Delete mailbox {email}?",
      sends: "sends replies",
      readOnly: "read only",
      ingestOff: "not receiving",
      autoIssues: "auto issues",
      inbound: "Incoming",
      outbound: "Outgoing",
      ingestOn: "Receiving mail",
      ingestOffSub: "Not receiving mail",
      readOnlyChannel: "Read only",
      sendVia: "Sent through your organization's email service",
      sendOff: "Sending is off",
      sendTest: "Send test message",
      sendTestPrompt: "Send a test message to which address?",
      sendTestFailed: "The test message could not be sent: {reason}",
      unknown: "unknown",
      openInbox: "Open inbox",
      auditTrail: "Audit trail",
    },
    link: {
      title: "Project",
      none: "No project",
      noProjects: "No projects yet. Create one first under",
      projects: "Projects",
      help: "New mail to this address becomes work in the chosen project. Without one, a company with exactly one project uses that project; otherwise the work has no project.",
      failed: "Connecting to the project failed (HTTP {status}).",
    },
    health: {
      title: "Activity",
      loading: "Loading activity…",
      inbound24h: "Received 24h",
      inbound7d: "Received 7d",
      live: "Open threads",
      unread: "Unread",
      snoozed: "Snoozed",
      sent7d: "Sent 7d",
      oldest: "Oldest open",
      minutes: "{n}m",
      hours: "{n}h",
      days: "{n}d",
    },
    policy: {
      title: "AI rules in effect",
      faq: "FAQ / support",
      refund: "Refunds",
      security: "Security",
      confidence: "Confidence threshold: {value}",
      autoSend: "Sent automatically: {list}",
      none: "none",
      approval: "Approval required: {count} categories",
      blocked: "Blocked: {count} categories",
      ceiling: {
        auto_send: "Sent automatically",
        draft: "Draft",
        suggest: "Suggestion",
        off: "Blocked",
      },
    },
    empty: "No mailboxes yet. Your company has the automatic system mailbox that catches all incoming mail. Add one to sort mail by the address it was sent to.",
    risk: {
      normal_support: "Normal support",
      bug_report: "Bug report",
      refund_billing: "Refund / billing",
      legal: "Legal",
      security: "Security",
      outage: "Outage",
      angry_customer: "Angry customer",
      vip_customer: "VIP customer",
      account_access: "Account access",
      data_privacy: "Data / privacy",
    },
    autoAssignPlaceholder: "off / round_robin / sticky",
  },
};

export const mailboxSetupNl: typeof mailboxSetupEn = {
  projectSetupMailbox: {
    title: "Mailbox koppelen",
    intakeOther: "Andere werkbronnen",
    progress: "Stap {current} van {total}",
    progressDone: "Klaar",
    dialogLabel: "Mailbox koppelen aan {project}",
    back: "Terug",
    next: "Verder",
    cancel: "Annuleren",
    close: "Sluiten",
    loading: "Laden…",
    loadError: "De mailboxopties konden niet worden geladen. Controleer je verbinding en probeer het opnieuw.",
    retry: "Opnieuw proberen",
    technicalCode: "Code: {code}",
    providerName: {
      gmail: "Google",
      office365: "Microsoft",
      internal: "Doorsturen",
      forward: "FixControl-adres",
    },
    providerLabel: {
      gmail: "Gmail / Google Workspace",
      office365: "Microsoft 365 / Outlook",
      internal: "Doorgestuurde mail",
      forward: "Doorsturen naar FixControl",
    },
    fieldError: {
      address_required: "Vul het adres in.",
      address_invalid: "Vul een volledig e-mailadres in, zoals support@bedrijf.nl.",
      shared_invalid: "Vul een volledig e-mailadres in, zoals support@bedrijf.nl.",
      account_required: "Kies een mailbox om verder te gaan.",
      existing_required: "Kies een mailbox om verder te gaan.",
      display_name_required: "Vul een naam in.",
    },
    fieldLabel: {
      address: "Adres van de mailbox",
    },
    start: {
      heading: "Welke mailbox moet werk voor dit project ontvangen?",
      intro: "Mail die in de mailbox binnenkomt, wordt werk in {project}. Standaard gaat een antwoord pas de deur uit als iemand het verstuurt of goedkeurt.",
      newTitle: "Nieuwe mailbox koppelen",
      newBody: "Log in met Microsoft 365 of Google, of stuur mail door vanaf een ander adres.",
      existingTitle: "Bestaande mailbox gebruiken",
      existingBody: "Kies een mailbox die je organisatie al heeft gekoppeld.",
      existingCount: "{count} beschikbaar",
      existingNone: "Nog geen beschikbaar",
      newOrgOnly: "Hiervoor is een beheerder van je organisatie nodig.",
      existingOnly: "Je kunt dit project koppelen aan mailboxen die je organisatie al heeft. Voor een nieuw Google- of Microsoft-account is een beheerder van je organisatie nodig.",
      existingOnlyNamed: "Je kunt dit project koppelen aan mailboxen die je organisatie al heeft. Voor een nieuw Google- of Microsoft-account is een beheerder van je organisatie nodig: {names}.",
    },
    provider: {
      heading: "Waar staat deze mailbox?",
      intro: "Kies de dienst waar je mailbox draait. Je logt in op de eigen pagina van die dienst; FixControl ziet je wachtwoord nooit.",
      hint: {
        office365: "Werk- of schoolaccounts, Outlook.com en gedeelde mailboxen.",
        gmail: "Google Workspace- en Gmail-accounts.",
        internal: "Stuur mail vanaf een adres door naar een mailbox die FixControl al leest. De mail wordt gesorteerd op het adres waar hij naartoe is gestuurd.",
        forward: "Geen login nodig. FixControl geeft je een adres, en je stuurt je mail daarheen door vanuit Gmail, Outlook of een andere maildienst.",
      },
      notConfigured: "Inloggen met {provider} is op deze installatie nog niet ingesteld. Degene die FixControl voor je organisatie beheert, kan dit aanzetten.",
      askAdmin: "Vraag het je beheerder",
      askAdminBody: "Vraag degene die FixControl voor je organisatie beheert om inloggen met {provider} in te stellen. Tot die tijd kun je een mailbox gebruiken die al gekoppeld is.",
      useExisting: "Gebruik een bestaande mailbox",
      noCreateRight: "Alleen mensen die bedrijfsverbindingen mogen toevoegen, kunnen een nieuwe mailbox koppelen. Vraag het een beheerder van je organisatie, of gebruik een mailbox die al gekoppeld is.",
      internalNeedsAccount: "Beschikbaar zodra er een Microsoft- of Google-mailbox aan je organisatie is gekoppeld.",
      internalOrgOnly: "Alleen een beheerder van je organisatie kan een nieuw doorstuuradres toevoegen. Je kunt er een gebruiken dat je organisatie al heeft.",
      forwardOrgOnly: "Alleen een beheerder van je organisatie kan een nieuwe mailbox toevoegen. Je kunt er een gebruiken die je organisatie al heeft.",
      unavailable: "Niet beschikbaar",
    },
    explain: {
      heading: "Voordat je verdergaat naar {provider}",
      intro: "Je logt in op de eigen pagina van {provider}. FixControl ziet je wachtwoord nooit.",
      permissionsTitle: "Waar FixControl om vraagt",
      permissions: {
        office365: "FixControl vraagt Microsoft toestemming om berichten te lezen in de mailbox die je kiest en, als je dat toestaat, antwoorden via die mailbox te versturen. Je kunt dit later weer intrekken.",
        gmail: "FixControl vraagt Google toestemming om berichten te lezen in de mailbox die je kiest en, als je dat toestaat, antwoorden via die mailbox te versturen. Je kunt dit later weer intrekken.",
      },
      neverDelete: "FixControl verwijdert nooit mail.",
      scopeTitle: "Bedrijf of project?",
      scope: "Deze verbinding hoort bij je organisatie. Je koppelt een van de mailboxen daarvan aan dit project.",
      continue: {
        office365: "Verder naar Microsoft",
        gmail: "Verder naar Google",
      },
      leaving: "Na het inloggen kom je terug bij deze stap.",
    },
    select: {
      heading: "Welke mailbox?",
      intro: "{provider} is verbonden. Kies de mailbox die werk voor {project} moet ontvangen.",
      accountsLabel: "Mailboxen die je kunt gebruiken",
      signedInAs: "Ingelogd als {address}",
      justConnected: "Net verbonden",
      sharedLabel: "Gedeelde mailbox (optioneel)",
      sharedHint: "Wil je een gedeelde mailbox gebruiken die dit account kan openen? Vul dan het adres in. Laat leeg om {address} te gebruiken.",
      internalHeading: "Welk adres stuurt mail door?",
      internalIntro: "Vul het adres in waar je klanten naartoe mailen. Stel daar doorsturen in naar een mailbox die FixControl al leest.",
      internalLabel: "Adres dat mail doorstuurt",
      internalPlaceholder: "support@bedrijf.nl",
      forwardHeading: "Naar welk adres mailen je klanten?",
      forwardIntro: "Vul je supportadres in. Na het koppelen geeft FixControl je een adres om die mail naartoe door te sturen.",
      forwardLabel: "Je supportadres",
      noAccount: "We konden het account waarmee je net bent ingelogd niet vinden. Log opnieuw in, of vraag een beheerder de verbinding te controleren.",
      signInAgain: "Opnieuw inloggen",
      usedBy: "In gebruik bij {project}",
      usedByOther: "In gebruik bij een ander project",
      usedHere: "Al gekoppeld aan dit project",
      needsReconnect: "Inloggen werkt niet meer",
      oauthHeading: "Inloggen met {provider} is niet afgerond",
      tryAgain: "Opnieuw proberen",
      reviewPermissions: "Toestemming bekijken",
    },
    existing: {
      heading: "Kies een bedrijfsmailbox",
      intro: "Deze mailboxen zijn al aan je organisatie gekoppeld. Een mailbox levert werk aan één project tegelijk.",
      listLabel: "Bedrijfsmailboxen",
      empty: "Je organisatie heeft nog geen mailbox die hier gebruikt kan worden.",
      connectNew: "Nieuwe mailbox koppelen",
      accountNotUsed: "Verbonden {provider}-account, nog door geen project gebruikt",
      system: "Systeemmailbox van FixControl",
      internalBadge: "Doorgestuurd",
    },
    behavior: {
      heading: "Hoe moet deze mailbox werken in {project}?",
      intake: "Nieuwe mail wordt werk in {project}",
      intakeHelp: "Dat is precies wat het koppelen van een mailbox aan een project doet, dus dit staat altijd aan.",
      replies: "Antwoorden via deze mailbox toestaan",
      repliesHelpOff: "Uit: FixControl leest alleen mail. Je kunt dit later aanzetten.",
      repliesHelpOn: "Standaard verstuurt een mens elk antwoord. Of een antwoord eerst goedgekeurd moet worden, en of de AI zelf mag versturen, stel je in op de pagina Goedkeuringen & regels van het project, onder Antwoorden aan klanten (bedrijfsbrede standaard: Klantcommunicatie → Verzenden & goedkeuring).",
      repliesInternal: "Vanaf een doorstuuradres kunnen geen antwoorden worden verstuurd.",
      repliesForward: "Antwoorden gaan uit vanaf het FixControl-adres van deze mailbox, onder de naam hieronder. Het antwoord van je klant komt direct hier terug.",
      replyIdentityForward: "{name} <het FixControl-adres van deze mailbox>",
      repliesShared: "Deze instelling hoort bij de bedrijfsmailbox, dus hij geldt ook waar de mailbox verder wordt gebruikt.",
      displayName: "Naam die collega's zien",
      displayNameHelp: "Bijvoorbeeld \"Gastenservice\". Zichtbaar in de inbox en bij werkitems.",
      replyIdentity: "Antwoorden worden verstuurd als",
      replyIdentityOff: "Antwoorden staan uit, dus vanaf dit adres wordt niets verstuurd.",
      connect: "Koppelen en testen",
    },
    test: {
      heading: "Verbinding testen",
      intro: "FixControl controleert of het {address} kan gebruiken voor {project}.",
      running: "Bezig met controleren…",
      allGood: "Alles werkt.",
      warnOnly: "Het werkt. Er is iets om in de gaten te houden.",
      waitingFirst: "Ingesteld, wacht op de eerste e-mail. Stuur een bericht naar {address} om te zien dat het aankomt.",
      failed: "Er is iets dat je aandacht nodig heeft voordat er mail kan binnenkomen.",
      testAgain: "Opnieuw testen",
      finish: "Afronden",
      finishLater: "Sluiten en later oplossen",
      connectError: "De mailbox kon niet worden gekoppeld.",
      runError: "De test kon nu niet worden uitgevoerd.",
      linkedElsewhere: "Dit adres levert al werk aan {project}. Ontkoppel het daar eerst, of kies een andere mailbox.",
    },
    forward: {
      title: "Stuur je mail door naar dit adres",
      copy: "Kopiëren",
      copied: "Gekopieerd",
      gmailTitle: "In Gmail",
      gmailSteps: "Instellingen → Alle instellingen bekijken → Doorsturen en POP/IMAP → Een doorstuuradres toevoegen, en plak het adres hierboven. Gmail stuurt daarna een bevestigingscode naar FixControl, en die verschijnt hier. Vul hem in bij Gmail en kies \"Een kopie van inkomende e-mail doorsturen\".",
      gmailCode: "Gmail heeft bevestigingscode {code} gestuurd. Vul hem in bij de doorstuurinstellingen van Gmail, of bevestig met de link.",
      gmailLinkOnly: "Gmail heeft een bevestigingslink gestuurd. Open hem om het doorsturen te starten.",
      gmailConfirmLink: "Bevestigen in Gmail",
      outlookTitle: "In Microsoft 365 / Outlook",
      outlookSteps: "Maak een regel die inkomende mail doorstuurt naar het adres hierboven, of vraag je beheerder om doorsturen op de mailbox in te stellen. Mogelijk moet je beheerder doorsturen naar externe adressen toestaan.",
      keepCopy: "Houd een kopie in je eigen mailbox: FixControl werkt met de doorgestuurde kopie.",
    },
    check: {
      label: {
        connection: "Inloggen werkt",
        mailbox_readable: "Kan berichten lezen",
        address_matches_connection: "Adres hoort bij deze inlog",
        ingest_enabled: "Mail ontvangen staat aan",
        project_resolution: "Nieuwe mail komt in dit project",
        recent_ingest: "Onlangs mail ontvangen",
        reply_channel: "Antwoorden kunnen worden verstuurd",
        provider_health: "Verbinding werkt",
        test_message: "Testbericht",
      },
      status: {
        pass: "In orde",
        warn: "Let op",
        fail: "Aandacht nodig",
        skipped: "Niet nodig",
      },
      message: {
        connection: {
          pass: "FixControl is ingelogd.",
          skipped: "Overgeslagen totdat het inloggen werkt.",
          internal: "Niet nodig voor een doorstuuradres.",
          forward: "Niet nodig: je mail wordt naar FixControl doorgestuurd.",
        },
        mailbox_readable: {
          pass: "FixControl kan nieuwe berichten in deze mailbox lezen.",
          skipped: "Overgeslagen totdat het inloggen werkt.",
          internal: "Niet nodig voor een doorstuuradres.",
          forward: "Niet nodig: je mail wordt naar FixControl doorgestuurd.",
        },
        address_matches_connection: {
          pass: "Dit adres is de mailbox die deze inlog leest.",
          skipped: "Overgeslagen totdat het inloggen werkt.",
          internal: "Niet nodig voor een doorstuuradres.",
          forward: "Niet nodig: je mail wordt naar FixControl doorgestuurd.",
        },
        ingest_enabled: {
          pass: "Nieuwe mail naar dit adres wordt opgehaald.",
        },
        project_resolution: {
          pass: "Nieuwe mail naar dit adres wordt werk in dit project.",
        },
        recent_ingest: {
          pass: "Er is onlangs mail binnengekomen.",
          never: "Er is nog geen mail binnengekomen. Stuur een e-mail naar dit adres om te zien dat hij aankomt.",
          stale: "De afgelopen 7 dagen is er geen mail binnengekomen.",
          waiting_first: "Er is nog geen mail binnengekomen, dus nog niets bewijst dat het doorsturen werkt. Stuur een e-mail naar dit adres en kijk of hij aankomt.",
        },
        reply_channel: {
          pass: "Goedgekeurde antwoorden kunnen worden verstuurd.",
          replies_off: "Antwoorden staan uit voor deze mailbox.",
          skipped: "Antwoorden staan uit voor deze mailbox.",
        },
      },
    },
    fix: {
      reconnect: "Opnieuw verbinden",
      review_permissions: "Toestemming herstellen",
      choose_mailbox: "Andere mailbox kiezen",
      enable_ingest: "Mail ontvangen aanzetten",
      link_project: "Aan dit project koppelen",
      configure_outbound: "Uitgaande e-mail instellen",
      confirm_test_message: "Testbericht versturen",
      retry: "Opnieuw proberen",
    },
    done: {
      heading: "Mailbox gekoppeld",
      body: "{address} is nu gekoppeld aan {project}.",
      bodyAttention: "{address} is gekoppeld aan {project}, maar heeft nog aandacht nodig. Je kunt het oplossen vanaf de mailboxkaart.",
      bodyWaiting: "{address} is gekoppeld aan {project}. Het is nog niet bewezen: stuur een e-mail naar {address} en kijk of hij aankomt.",
      next: "Volgende stap: {step}",
      done: "Klaar",
    },
    cards: {
      title: "Mailboxen",
      empty: "Nog geen mailbox gekoppeld.",
      emptyBody: "Ontvang e-mail van klanten als werk in dit project.",
      connect: "Mailbox koppelen",
      connectAnother: "Nog een mailbox koppelen",
      viewProjectInbox: "Projectinbox bekijken",
      usedFor: "Gebruikt voor: {list}",
      usedForIntake: "Binnenkomend werk",
      usedForReplies: "Goedgekeurde antwoorden",
      lastChecked: "Laatst gecontroleerd {when}",
      neverChecked: "Nog niet gecontroleerd",
      test: "Verbinding testen",
      manage: "Beheren",
      disconnect: "Ontkoppelen van dit project",
      noRight: "Alleen een beheerder van je organisatie of van dit project kan een mailbox koppelen. Vraag het een van hen.",
      noRightNamed: "Alleen een beheerder van je organisatie ({names}) of van dit project kan een mailbox koppelen. Vraag het een van hen.",
      readOnly: "Je kunt deze mailbox zien. Alleen een beheerder van je organisatie of van dit project kan hem testen of wijzigen.",
      waitingHint: "Stuur een e-mail naar {address} om te zien dat het doorsturen werkt.",
      status: {
        working: "Werkt",
        waiting: "Ingesteld, wacht op de eerste e-mail",
        untested: "Nog niet getest",
        attention: "Aandacht nodig",
        reconnect: "Opnieuw verbinden nodig",
        disabled: "Uitgeschakeld",
      },
      manageTitle: "{address} beheren",
      manageIntro: "Hoe deze mailbox werkt in {project}. De naam en of antwoorden zijn toegestaan horen bij de mailbox van het bedrijf zelf, dus een wijziging hier ziet iedereen in je organisatie.",
      save: "Opslaan",
      saved: "Opgeslagen",
      disconnectTitle: "{address} ontkoppelen van {project}?",
      disconnectBody: "Nieuwe mail naar dit adres komt dan niet meer in dit project binnen. De bedrijfsmailbox zelf blijft gekoppeld, en andere projecten merken er niets van.",
      disconnectConfirm: "Ontkoppelen",
      testResultTitle: "Testresultaat",
      hideResult: "Resultaat verbergen",
    },
  },
  settingsMailboxes: {
    eyebrow: "Communicatie",
    title: "Mailboxen",
    subtitle: "Alle mailboxen die je organisatie gebruikt: waar de mail vandaan komt, of er goedgekeurde antwoorden vanaf mogen, en naar welk project de mail gaat. Mailboxen zijn van het hele bedrijf; per project kies je welke daar werk aanlevert.",
    loading: "Laden…",
    loadError: "De mailboxen konden niet worden geladen (HTTP {status}).",
    loadErrorGeneric: "De mailboxen konden niet worden geladen.",
    count: "{count} mailboxen",
    countOne: "1 mailbox",
    outboundChannel: "Uitgaande e-mail: {provider}",
    dryRun: "testmodus",
    newMailbox: "Nieuwe mailbox",
    cancel: "Annuleren",
    create: {
      title: "Nieuwe mailbox",
      sub: "Gedeelde mailboxen zijn zichtbaar voor het hele bedrijf. Een persoonlijke mailbox hoort bij één persoon.",
      fromProject: "Koppel je een mailbox voor een project? Gebruik dan de begeleide stappen in het project.",
      openWizard: "Begeleide stappen openen",
      submit: "Mailbox aanmaken",
      error: "De mailbox kon niet worden aangemaakt.",
      linkFailed: "De mailbox is aangemaakt, maar koppelen aan het project is mislukt: {error}",
      noProviders: "Er is nog geen Microsoft- of Google-mailbox gekoppeld. Koppel er eerst een onder Kanalen, of maak een doorstuuradres aan.",
      connectProviders: "Naar Kanalen",
    },
    field: {
      kind: "Soort",
      provider: "Waar de mail vandaan komt",
      email: "E-mailadres",
      displayName: "Naam die collega's zien",
      visibility: "Wie kan hem zien",
      owner: "Eigenaar",
      ownerHelp: "Verplicht voor een persoonlijke of privémailbox.",
      ownerNone: "Geen eigenaar",
      ownerMe: "Ik ({name})",
      ownerUnavailable: "Hier kun je alleen jezelf kiezen; een beheerder kan elke collega kiezen.",
      project: "Koppelen aan project (optioneel)",
      projectNone: "Geen project",
      projectHelp: "Nieuwe mail naar dit adres wordt dan werk in dat project.",
      color: "Kleur",
      signature: "Handtekening (optioneel, platte tekst)",
      autoAssign: "Automatisch toewijzen",
    },
    kind: {
      shared: "Gedeeld",
      personal: "Persoonlijk",
      system: "Systeem",
    },
    provider: {
      gmail: "Gmail / Google Workspace",
      office365: "Microsoft 365 / Outlook",
      internal: "Doorgestuurd (verstuurt niet)",
      forward: "Doorgestuurd naar FixControl",
      connectedAs: "{provider} ({addresses})",
    },
    visibility: {
      tenant: "Hele bedrijf",
      team: "Team (voorlopig gelijk aan hele bedrijf)",
      private: "Privé (alleen eigenaar)",
      tenantShort: "Hele bedrijf",
      teamShort: "Team",
      privateShort: "Privé",
    },
    toggle: {
      send: "Antwoorden toestaan",
      sendHelp: "Er mogen antwoorden vanaf dit adres verstuurd worden. Goedkeuring en verzenden door de AI stel je per project in bij Goedkeuringen & regels → Antwoorden aan klanten, met de bedrijfsbrede standaard onder Klantcommunicatie → Verzenden & goedkeuring.",
      sendInternal: "Een doorstuuradres verstuurt nooit.",
      sendForward: "Antwoorden gaan uit vanaf het FixControl-adres van deze mailbox, onder de naam ervan.",
      ingest: "Mail ontvangen",
      ingestHelp: "Uit: nieuwe mail wordt genegeerd; de mailbox blijft bewaard voor de geschiedenis.",
      autoCreateIssues: "Issues automatisch aanmaken",
      autoCreateIssuesHelp: "Herkent FixControl een duidelijke bug, featureverzoek of andere uitvoerbare engineeringtaak, dan kan het automatisch een issue aanmaken in het gekoppelde project. Supportvragen en irrelevante mail blijven in de inbox.",
      autoCreateOff: "Uit — je maakt zelf een issue",
      autoCreateEngineering: "Bugs en wensen",
      autoCreateAll: "Elke klantvraag",
      autoCreateOffHelp: "Niets wordt vanzelf een issue. Gebruik \"Issue aanmaken\" bij een mail als je er een wilt.",
      autoCreateEngineeringHelp: "Een duidelijke bug of wens wordt een issue in het gekoppelde project. Vragen blijven in de inbox.",
      autoCreateAllHelp: "Elke klantvraag wordt een issue in het gekoppelde project, ook gewone vragen; een vraag krijgt daarnaast nog steeds het AI-antwoordvoorstel. Spam, automatische antwoorden en een kaal \"bedankt\" nooit, en een gesprek dat al een issue heeft krijgt geen tweede.",
    },
    card: {
      edit: "Bewerken",
      save: "Opslaan",
      saved: "Opgeslagen",
      delete: "Verwijderen",
      deleteConfirm: "Mailbox {email} verwijderen?",
      sends: "verstuurt antwoorden",
      readOnly: "alleen lezen",
      ingestOff: "ontvangt niet",
      autoIssues: "auto-issues",
      inbound: "Binnenkomend",
      outbound: "Uitgaand",
      ingestOn: "Ontvangt mail",
      ingestOffSub: "Ontvangt geen mail",
      readOnlyChannel: "Alleen lezen",
      sendVia: "Verstuurd via de e-maildienst van je organisatie",
      sendOff: "Versturen staat uit",
      sendTest: "Testbericht versturen",
      sendTestPrompt: "Testbericht versturen naar welk adres?",
      sendTestFailed: "Het testbericht kon niet worden verstuurd: {reason}",
      unknown: "onbekend",
      openInbox: "Inbox openen",
      auditTrail: "Audittrail",
    },
    link: {
      title: "Project",
      none: "Geen project",
      noProjects: "Nog geen projecten. Maak er eerst een aan onder",
      projects: "Projecten",
      help: "Nieuwe mail naar dit adres wordt werk in het gekozen project. Zonder project gebruikt een bedrijf met precies één project dat project; anders krijgt het werk geen project.",
      failed: "Koppelen aan het project is mislukt (HTTP {status}).",
    },
    health: {
      title: "Activiteit",
      loading: "Activiteit laden…",
      inbound24h: "Ontvangen 24u",
      inbound7d: "Ontvangen 7d",
      live: "Open gesprekken",
      unread: "Ongelezen",
      snoozed: "Gesnoozed",
      sent7d: "Verzonden 7d",
      oldest: "Oudste open",
      minutes: "{n}m",
      hours: "{n}u",
      days: "{n}d",
    },
    policy: {
      title: "Geldende AI-regels",
      faq: "FAQ / support",
      refund: "Terugbetalingen",
      security: "Beveiliging",
      confidence: "Vertrouwensdrempel: {value}",
      autoSend: "Automatisch verstuurd: {list}",
      none: "geen",
      approval: "Goedkeuring vereist: {count} categorieën",
      blocked: "Geblokkeerd: {count} categorieën",
      ceiling: {
        auto_send: "Automatisch versturen",
        draft: "Concept",
        suggest: "Suggestie",
        off: "Geblokkeerd",
      },
    },
    empty: "Nog geen mailboxen. Je bedrijf heeft de automatische systeemmailbox die alle binnenkomende mail opvangt. Voeg er een toe om mail te sorteren op het adres waar hij naartoe is gestuurd.",
    risk: {
      normal_support: "Normale support",
      bug_report: "Bugmelding",
      refund_billing: "Terugbetaling / facturatie",
      legal: "Juridisch",
      security: "Beveiliging",
      outage: "Storing / uitval",
      angry_customer: "Boze klant",
      vip_customer: "VIP-klant",
      account_access: "Accounttoegang",
      data_privacy: "Data / privacy",
    },
    autoAssignPlaceholder: "off / round_robin / sticky",
  },
};
